// @ts-nocheck
import FontStyleConfig from './FontStyleConfig/FontStyleConfig';
import ColorConfig from './ColorConfig/ColorConfig';
import Toggle from '../../Common/Toggle/Toggle';
import OutputDataConfig from './OutputDataConfig/OutputDataConfig';
import Outputs from '../../GlobalSettings/Templates/Outputs/Outputs';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setHighlightColor } from '../../../redux/slices/color/color';
import { setHeadingFont, setBodyFont } from '../../../redux/slices/font/font';
import { ErrorToast } from '../../../utils/alerts';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  getTenantSettings,
  setTenantSettings,
  setTripSettings,
  getTripSettings
} from '../../../services/tenant/tenant';
import {
  parseTemplateSettingValue,
  resolveTemplateSettings
} from '../../../utils/templateSettings';
import './TemplatesPanel.css';

const SETTINGCODE = 'TEMPLATEFIELDS';

/**
 * Functional React component for rendering the templates panel.
 *
 * @namespace Components
 *
 * @param {object} props - The properties of the component.
 * @param {string} props.id - The ID for the component.
 *
 * @returns {JSX.Element} - The rendered component.
 */
const TemplatesPanel = ({ id, isGlobal = false }) => {
  const [selectedOutput, setSelectedOutput] = useState('Crios');
  const [showImagesInPdf, setShowImagesInPdf] = useState(true);
  const [cherryServicesExpanded, setCherryServicesExpanded] = useState(false);
  const [savingCherrySetting, setSavingCherrySetting] = useState(false);
  const { t } = useTranslation(['trip', 'common', 'settings']);
  const { tripNavigation, cherryServicesExpandedSetting } = useFlags();
  const dispatch = useDispatch();
  const highlightColor = useSelector((state) => state.color.highlightColor);
  const headingFont = useSelector((state) => state.font.headingFont);
  const bodyFont = useSelector((state) => state.font.bodyFont);
  const tripId = useSelector((state) => state.trip?.tripid);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const globalSettings = await getTenantSettings(SETTINGCODE);
        const tripSettings = isGlobal
          ? null
          : await getTripSettings(tripId, SETTINGCODE);

        const globalValue = globalSettings?.data?.settingvalue;
        const tripValue = tripSettings?.data?.settingvalue;
        const resolved = resolveTemplateSettings({
          globalSettingValue: globalValue,
          tripSettingValue: tripValue,
          useTripTemplate: !isGlobal
        });

        dispatch(setHeadingFont(resolved.headingfont));
        dispatch(setBodyFont(resolved.bodyfont));
        dispatch(setHighlightColor(resolved.color));
        setSelectedOutput(resolved.name);
        setShowImagesInPdf(resolved.showImagesInPdf);
        setCherryServicesExpanded(resolved.cherryServicesExpanded);
      } catch (error) {
        ErrorToast(t('common:errorLoadingSettings'));
      }
    };

    fetchSettings();
  }, [dispatch, t, isGlobal, tripId]);

  const saveSettings = async (newData) => {
    try {
      const current = isGlobal
        ? await getTenantSettings(SETTINGCODE)
        : await getTripSettings(tripId, SETTINGCODE);
      const settings = {
        ...parseTemplateSettingValue(current?.data?.settingvalue),
        ...newData
      };

      const response = isGlobal
        ? await setTenantSettings(SETTINGCODE, [{ ...settings }])
        : await setTripSettings(tripId, SETTINGCODE, [{ ...settings }]);
      if (!response.data || response?.error) {
        throw new Error('Could not save template settings');
      }
      return true;
    } catch (error) {
      ErrorToast(t('common:errorSavingSettings'));
      return false;
    }
  };

  const updateSettings = (evt, newData) =>
    saveSettings({
      name: newData?.name ?? selectedOutput,
      headingfont: newData?.headingfont ?? headingFont,
      bodyfont: newData?.bodyfont ?? bodyFont,
      color: newData?.color ?? highlightColor,
      ...newData
    });

  const handleColorChange = (color) => {
    dispatch(setHighlightColor(color));
    updateSettings(null, { color });
  };

  const handleFontChange = (type, font) => {
    if (type === 'heading') {
      dispatch(setHeadingFont(font));
      updateSettings(null, { headingfont: font });
    } else if (type === 'body') {
      dispatch(setBodyFont(font));
      updateSettings(null, { bodyfont: font });
    }
  };

  const onChangeOutput = (output) => {
    setSelectedOutput(output);
    updateSettings(null, { name: output });
  };

  const handlePdfImageSettingChange = (event) => {
    const nextValue = event.target.checked;
    setShowImagesInPdf(nextValue);
    updateSettings(null, { showImagesInPdf: nextValue });
  };

  const handleCherrySettingChange = async (event) => {
    const nextValue = event.target.checked;
    const previousValue = cherryServicesExpanded;
    setCherryServicesExpanded(nextValue);
    setSavingCherrySetting(true);
    const saved = await saveSettings({
      cherryServicesExpanded: nextValue,
      ...(!isGlobal && { cherryServicesExpandedOverride: true })
    });
    if (!saved) {
      setCherryServicesExpanded(previousValue);
    }
    setSavingCherrySetting(false);
  };

  const outputSelector = (
    <Outputs
      selectedOutput={selectedOutput}
      setSelectedOutput={(output) => onChangeOutput(output)}
      label={
        isGlobal
          ? t('settings:templates')
          : t(
              'settings:selectTemplateDesignForThisTrip',
              "Select a template design for this trip's proposals"
            )
      }
    />
  );

  return (
    <Box
      id={id}
      className={`templates-panel ${tripNavigation ? 'templates-panel--fixed' : ''}`}
      data-testid='templates-panel'
    >
      <Box sx={{ width: '100%' }} className='template-config-panel'>
        {!isGlobal && (
          <Box sx={{ width: '100%' }} className='template-pdf-settings'>
            <h6 className='text-lg-semibold template-config-panel__title'>
              {t('settings:finalItinerarySettings', 'Final Itinerary Settings')}
            </h6>
            <Box className='template-pdf-setting__card'>
              <span className='template-pdf-setting__label'>
                {t('settings:showImagesInPdf', 'Show Images in PDF')}
              </span>
              <Toggle
                id='show-image-in-pdf-toggle'
                checked={showImagesInPdf}
                onChange={handlePdfImageSettingChange}
              />
            </Box>
          </Box>
        )}
        <Box className='template-config-panel__section'>
          {!isGlobal && (
            <h6 className='text-lg-semibold template-config-panel__title'>
              {t('settings:proposalSettings', 'Proposal Settings')}
            </h6>
          )}
          {cherryServicesExpandedSetting ? (
            <Box
              className={`template-proposal-controls ${isGlobal ? '' : 'template-proposal-controls--trip'}`}
            >
              {outputSelector}
              <Box className='template-cherry-setting-slot'>
                {selectedOutput === 'Cherry' && (
                  <Box className='template-cherry-setting'>
                    <Toggle
                      id={`${id}-cherry-services-expanded`}
                      checked={cherryServicesExpanded}
                      onChange={handleCherrySettingChange}
                      disabled={savingCherrySetting}
                      inputProps={{ role: 'switch' }}
                    />
                    <label htmlFor={`${id}-cherry-services-expanded`}>
                      {t('settings:alwaysShowServicesExpanded')}
                    </label>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            outputSelector
          )}
        </Box>

        {isGlobal && (
          <Box className='template-style-container'>
            <ColorConfig event={handleColorChange} />
            <FontStyleConfig onFontChange={handleFontChange} />
          </Box>
        )}
      </Box>
      <Box className='template-output-panel'>
        <OutputDataConfig
          id='output-template-preview'
          selectedOutput={selectedOutput}
        />
      </Box>
    </Box>
  );
};

TemplatesPanel.propTypes = {
  id: PropTypes.string,
  isGlobal: PropTypes.bool
};

export default TemplatesPanel;
